import { Subtitle, TokenUsage } from './types';

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

export const base64ToBlob = (base64: string, mimeType: string): Blob => {
  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: mimeType });
};

export const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    video.onerror = () => {
      reject("Invalid video file");
    };
    video.src = URL.createObjectURL(file);
  });
};

// --- Cost Calculation Logic ---

export const calculateAICost = (usage: TokenUsage, model: string = 'flash'): string => {
  // Pricing per 1 Million Tokens (Approximate Google Cloud Pricing)
  let inputPrice = 0.10;
  let outputPrice = 0.40;

  if (model.includes('pro')) {
    // Pro Model is significantly more expensive
    inputPrice = 1.25; 
    outputPrice = 5.00;
  }

  const inputCost = (usage.promptTokenCount / 1_000_000) * inputPrice;
  const outputCost = (usage.candidatesTokenCount / 1_000_000) * outputPrice;
  
  const total = inputCost + outputCost;

  if (total > 0 && total < 0.0001) {
    return "< $0.0001";
  }

  return `$${total.toFixed(5)}`;
};

export const calculateRawCost = (usage: TokenUsage, model: string = 'flash'): number => {
    let inputPrice = 0.10;
    let outputPrice = 0.40;
  
    if (model.includes('pro')) {
      inputPrice = 1.25; 
      outputPrice = 5.00;
    }
  
    const inputCost = (usage.promptTokenCount / 1_000_000) * inputPrice;
    const outputCost = (usage.candidatesTokenCount / 1_000_000) * outputPrice;
    return inputCost + outputCost;
};

// --- Audio Extraction Logic ---

const audioBufferToWav = (buffer: AudioBuffer): ArrayBuffer => {
  const numOfChan = buffer.numberOfChannels;
  const length = buffer.length * numOfChan * 2 + 44;
  const out = new ArrayBuffer(length);
  const view = new DataView(out);
  const channels = [];
  let sample;
  let offset = 0;
  let pos = 0;

  // Write WAV Header
  setUint32(0x46464952); // "RIFF"
  setUint32(length - 8); // file length - 8
  setUint32(0x45564157); // "WAVE"

  setUint32(0x20746d66); // "fmt " chunk
  setUint32(16); // length = 16
  setUint16(1); // PCM (uncompressed)
  setUint16(numOfChan);
  setUint32(buffer.sampleRate);
  setUint32(buffer.sampleRate * 2 * numOfChan); // avg. bytes/sec
  setUint16(numOfChan * 2); // block-align
  setUint16(16); // 16-bit (hardcoded in this parser)

  setUint32(0x61746164); // "data" - chunk
  setUint32(length - pos - 4); // chunk length

  // Write interleaved data
  for (let i = 0; i < buffer.numberOfChannels; i++)
    channels.push(buffer.getChannelData(i));

  while (pos < buffer.length) {
    for (let i = 0; i < numOfChan; i++) {
      sample = Math.max(-1, Math.min(1, channels[i][pos]));
      sample = (0.5 + sample < 0 ? sample * 32768 : sample * 32767) | 0;
      view.setInt16(44 + offset, sample, true);
      offset += 2;
    }
    pos++;
  }

  return out;

  function setUint16(data: number) {
    view.setUint16(pos, data, true);
    pos += 2;
  }
  function setUint32(data: number) {
    view.setUint32(pos, data, true);
    pos += 4;
  }
};

export const extractAudioAsBase64 = async (videoFile: File): Promise<string> => {
  const arrayBuffer = await videoFile.arrayBuffer();
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
  const wavBuffer = audioBufferToWav(audioBuffer);
  
  let binary = '';
  const bytes = new Uint8Array(wavBuffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

// --- Waveform Analysis & Sync Logic ---

export const detectVoiceOnset = async (file: File): Promise<number> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    
    const rawData = audioBuffer.getChannelData(0);
    const sampleRate = audioBuffer.sampleRate;
    
    const windowSize = 2048; 
    const rmsThreshold = 0.015; 
    const requiredConsecutiveWindows = 4; 
    
    let consecutiveTriggers = 0;
    let potentialOnsetPosition = -1;

    for (let i = 0; i < rawData.length; i += windowSize) {
      let sum = 0;
      const end = Math.min(i + windowSize, rawData.length);
      
      for (let j = i; j < end; j++) {
        sum += rawData[j] * rawData[j];
      }
      const rms = Math.sqrt(sum / (end - i));

      if (rms > rmsThreshold) {
        if (consecutiveTriggers === 0) {
            potentialOnsetPosition = i; 
        }
        consecutiveTriggers++;

        if (consecutiveTriggers >= requiredConsecutiveWindows) {
            const time = potentialOnsetPosition / sampleRate;
            return Math.max(0, time - 0.2); 
        }
      } else {
        consecutiveTriggers = 0;
        potentialOnsetPosition = -1;
      }
    }
    
    return 0; 
  } catch (e) {
    console.warn("Could not detect audio onset:", e);
    return 0;
  }
};

// --- Strict Time Format Helpers (Math based, no Date object) ---

export const parseTimeToSeconds = (timeStr: string): number => {
    if (!timeStr) return 0;
    const cleanStr = timeStr.replace(',', '.');
    const parts = cleanStr.split(':');
    
    let seconds = 0;
    if (parts.length === 3) {
      seconds = (parseFloat(parts[0]) * 3600) + (parseFloat(parts[1]) * 60) + parseFloat(parts[2]);
    } else if (parts.length === 2) {
      seconds = (parseFloat(parts[0]) * 60) + parseFloat(parts[1]);
    }
    
    return isFinite(seconds) ? seconds : 0;
};

export const secondsToSrtTime = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const milliseconds = Math.round((totalSeconds % 1) * 1000);

    const pad = (num: number, size: number) => num.toString().padStart(size, '0');
    return `${pad(hours, 2)}:${pad(minutes, 2)}:${pad(seconds, 2)},${pad(milliseconds, 3)}`;
};

export const shiftSubtitles = (subtitles: Subtitle[], offsetSeconds: number): Subtitle[] => {
  return subtitles.map(sub => {
    const startSec = Math.max(0, parseTimeToSeconds(sub.startTime) + offsetSeconds);
    const endSec = Math.max(0, parseTimeToSeconds(sub.endTime) + offsetSeconds);
    
    return {
      ...sub,
      startTime: secondsToSrtTime(startSec),
      endTime: secondsToSrtTime(endSec)
    };
  });
};

export const elasticSyncSubtitles = (
    subtitles: Subtitle[], 
    actualStart: number, 
    actualEnd: number
): Subtitle[] => {
    if (subtitles.length < 2) return subtitles;

    const currentStart = parseTimeToSeconds(subtitles[0].startTime);
    const currentEnd = parseTimeToSeconds(subtitles[subtitles.length - 1].endTime);
    const currentDuration = currentEnd - currentStart;
    
    if (currentDuration <= 0) return subtitles;

    const targetDuration = actualEnd - actualStart;
    const ratio = targetDuration / currentDuration;

    return subtitles.map(sub => {
        const oldStart = parseTimeToSeconds(sub.startTime);
        const oldEnd = parseTimeToSeconds(sub.endTime);

        // Linear mapping formula: y = y1 + slope * (x - x1)
        // NewTime = TargetStart + Ratio * (OldTime - CurrentStart)
        
        const newStart = actualStart + (ratio * (oldStart - currentStart));
        const newEnd = actualStart + (ratio * (oldEnd - currentStart));

        return {
            ...sub,
            startTime: secondsToSrtTime(newStart),
            endTime: secondsToSrtTime(newEnd)
        };
    });
};

// --- End Audio Extraction Logic ---

export const generateSrtContent = (subtitles: Subtitle[], useTranslation: boolean): string => {
  return subtitles
    .map((sub, index) => {
      const text = useTranslation && sub.translatedText ? sub.translatedText : sub.text;
      
      let startSec = parseTimeToSeconds(sub.startTime);
      let endSec = parseTimeToSeconds(sub.endTime);

      if (endSec <= startSec) {
        endSec = startSec + 2.0;
      }
      
      const startStr = secondsToSrtTime(startSec);
      const endStr = secondsToSrtTime(endSec);

      return `${index + 1}\n${startStr} --> ${endStr}\n${text.trim()}`;
    })
    .join('\n\n'); 
};

export const downloadSrt = (content: string, filename: string) => {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
